import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen?: boolean;
  open?: boolean; 
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  footer?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  open,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  loading = false,
  footer,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
}) => {
  const finalIsOpen = isOpen ?? open ?? false;
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const closeTimeout = 220; 

  const sizeClasses: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-full',
  };

  useEffect(() => {
    if (finalIsOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsVisible(true));
      document.body.style.overflow = 'hidden';
    } else if (isMounted) {
      setIsVisible(false);
      const t = window.setTimeout(() => {
        setIsMounted(false);
        document.body.style.overflow = 'unset';
      }, closeTimeout);
      return () => window.clearTimeout(t);
    }
    return () => {};
  }, [finalIsOpen]);

  // ESC key handling
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEsc && finalIsOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [finalIsOpen, closeOnEsc, onClose]);

  useEffect(() => {
    if (finalIsOpen && modalRef.current) {
      const el = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement | null;
      if (el) el.focus();
    }
  }, [finalIsOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdropClick) onClose();
  };

  if (!isMounted) return null;

  const backdropBase = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 overflow-y-auto';
  const modalBase = [
    'bg-white rounded-lg shadow-xl w-full max-h-[min(90dvh,90vh)] flex flex-col min-h-0',
    sizeClasses[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');


  const modalAnimationClasses = isVisible
    ? 'opacity-100 translate-y-0 scale-100'
    : 'opacity-0 translate-y-6 scale-95';

  return createPortal(
    <div
      className={backdropBase}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={modalRef}
        className={modalBase + ' transition-transform transition-opacity duration-200 ease-out ' + modalAnimationClasses}
        role="document"
        aria-hidden={!isVisible}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className={['flex shrink-0 items-center justify-between p-6 border-b border-gray-200', headerClassName].filter(Boolean).join(' ')}>
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
                disabled={loading}
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={['flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6', bodyClassName].filter(Boolean).join(' ')}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer */}
        {footer && (
          <div className={['flex shrink-0 items-center justify-end gap-3 p-6 border-t border-gray-200', footerClassName].filter(Boolean).join(' ')}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
